
import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, Box, Image, Spinner, Center, Text,
  IconButton, Flex, Badge, Input, List, ListItem,Button, FormControl, FormLabel, Select,useToast,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";

export default function EditActivationModal({
  isOpen,
  onClose,
  selectedStoreDetails,
  generatePresignedUrls,
  scoreBreakdown,
  onRefresh,

}) {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [s3Paths, setS3Paths] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const toast = useToast();

  // Product dropdown states
  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch products when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const res = await apiClient.get(API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.PRODUCTS);
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load products", err);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
    // Reset on open
    setSearchText("");
    setSelectedProduct(null);
    setShowDropdown(false);
  }, [isOpen]);

  // Fetch images when modal opens
  useEffect(() => {
    if (!isOpen || !selectedStoreDetails) return;
    const fetchImages = async () => {
      setIsLoading(true);
      setError("");
      setImages([]);
      setCurrentIndex(0);
      try {
        const storeId = selectedStoreDetails.storeId;
        const raw = new Date(selectedStoreDetails.auditedDate);
        const month = raw.getMonth() + 1;
        const year = raw.getFullYear();
        const auditedDate = `${year}-${String(month).padStart(2, "0")}-${String(raw.getDate()).padStart(2, "0")}`;
        const res = await apiClient.get(
          `${API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.IMAGES}?storeId=${storeId}&date=${auditedDate}`
        );
        const fetchedPaths = Array.isArray(res.data) ? res.data : [];
        if (fetchedPaths.length === 0) { setImages([]); return; }
        setS3Paths(fetchedPaths);  
        const presignedUrls = await generatePresignedUrls(fetchedPaths, month, year);
        setImages(presignedUrls);
      } catch (err) {
        setError("Failed to load images.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchImages();
  }, [isOpen, selectedStoreDetails]);

  // Filter products by search text
  const filteredProducts = products.filter((p) =>
    p.productName.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSearchText(product.productName);
    setShowDropdown(false);
  };

  const handlePrev = () =>
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const handleNext = () =>
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const handleAdd = async () => {
  if (!selectedProduct) return;
  setIsAdding(true);
  try {
    const storeId = selectedStoreDetails?.storeId;
    const raw = new Date(selectedStoreDetails?.auditedDate);
    const auditedDate = `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, "0")}-${String(raw.getDate()).padStart(2, "0")}`;
    const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    const fileName = s3Paths[currentIndex] || ""; 

    await apiClient.post(API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.ADD, {
      runId: Number(runId),
      storeId: Number(storeId),
      fileName: fileName,
      auditDate: auditedDate,
      productId: Number(selectedProduct.productId),
      productText: selectedProduct.productName,
      value: "Y",
      s3pathActualFile: fileName,
      s3pathAnnotatedFile: "",
      userId: Number(userId),
    });
    toast({ title: "Added successfully", status: "success", duration: 2000, isClosable: true });
     onRefresh?.(); 
    onClose();
   
  } catch (err) {
    toast({
      title: "Add failed",
      description: err.response?.data?.message || "Something went wrong",
      status: "error", duration: 3000, isClosable: true,
    });
  } finally {
    setIsAdding(false);
  }
};

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Edit Activation
          {images.length > 0 && (
            <Badge ml={3} colorScheme="blue" fontSize="sm">
              {currentIndex + 1} / {images.length}
            </Badge>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Flex direction="column" gap={4}>

            {/* Searchable Product Dropdown */}
            <Box position="relative">
              <Text fontWeight="semibold" fontSize="sm" mb={1}>Product</Text>
              {isLoadingProducts ? (
                <Center h="40px"><Spinner size="sm" color="blue.500" /></Center>
              ) : (
                <>
                <Flex gap={2}>
                  <Input
                    placeholder="Search product..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setShowDropdown(true);
                      setSelectedProduct(null);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    focusBorderColor="blue.400"
                    autoComplete="off"
                  />
                  <Button
                    colorScheme="blue"
                    onClick={handleAdd}
                    isDisabled={!selectedProduct}
                    px={6}
                  >
                  Add
                   </Button>
                  </Flex>
                  {showDropdown && filteredProducts.length > 0 && (
                    <List
                      position="absolute" zIndex={999} bg="white" w="100%"
                      border="1px solid" borderColor="gray.200"
                      borderRadius="md" boxShadow="md"
                      maxH="180px" overflowY="auto" mt={1}
                    >
                      {filteredProducts.map((p) => (
                        <ListItem
                          key={p.productId}
                          px={3} py={2}
                          cursor="pointer"
                          _hover={{ bg: "blue.50" }}
                          onMouseDown={() => handleSelectProduct(p)} // mouseDown fires before onBlur
                        >
                          {p.productName}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Box>

            {/* Images */}
            {isLoading ? (
              <Center h="350px"><Spinner size="xl" color="blue.500" /></Center>
            ) : error ? (
              <Center h="350px"><Text color="red.500">{error}</Text></Center>
            ) : images.length === 0 ? (
              <Center h="350px"><Text color="gray.400">No images available</Text></Center>
            ) : (
              <Box position="relative">
                <Box borderRadius="lg" overflow="hidden"
                  border="1px solid" borderColor="gray.200" bg="gray.50" h="350px"
                >
                  <Image
                    src={images[currentIndex]}
                    alt={`Activation ${currentIndex + 1}`}
                    objectFit="contain" w="100%" h="100%"
                  />
                </Box>

                {images.length > 1 && (
                  <>
                    <IconButton
                      icon={<FiChevronLeft size={24} />}
                      position="absolute" left={2} top="50%"
                      transform="translateY(-50%)"
                      onClick={handlePrev} borderRadius="full"
                      bg="white" boxShadow="md" _hover={{ bg: "gray.100" }}
                      aria-label="Previous"
                    />
                    <IconButton
                      icon={<FiChevronRight size={24} />}
                      position="absolute" right={2} top="50%"
                      transform="translateY(-50%)"
                      onClick={handleNext} borderRadius="full"
                      bg="white" boxShadow="md" _hover={{ bg: "gray.100" }}
                      aria-label="Next"
                    />
                  </>
                )}

                {images.length > 1 && (
                  <Flex justify="center" gap={2} mt={3}>
                    {images.map((_, i) => (
                      <Box key={i} w={2} h={2} borderRadius="full"
                        bg={i === currentIndex ? "blue.500" : "gray.300"}
                        cursor="pointer" onClick={() => setCurrentIndex(i)}
                        transition="background 0.2s"
                      />
                    ))}
                  </Flex>
                )}
              </Box>
            )}

          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}